import type { ComponentType } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Bot, BriefcaseBusiness, Library, Wallet } from 'lucide-react';

import type { SettingsSection } from '@/api/types';
import { AiModelsTab } from '@/routes/ai-models/AiModelsTab';
import { BudgetTab } from '@/routes/ai-usage/BudgetTab';
import { AssetCatalogTab } from '@/routes/configuration/AssetCatalogTab';
import { BusinessOpportunitiesTab } from '@/routes/configuration/BusinessOpportunitiesTab';
import { openTab, type PermissionKey } from '@/session/permissions';

export type ConfigTab = { key: string; name: string; icon: LucideIcon; Screen: ComponentType; permissions: PermissionKey[] };

/** Configuration screens of their own, shown as tabs before the settings sections.
 *  A tab shows to whoever holds any of its `permissions`; the settings sections need settings.view. */
export const CONFIG_TABS: readonly ConfigTab[] = [
  { key: 'ai-models', name: 'AI Models', icon: Bot, Screen: AiModelsTab, permissions: ['ai.models.view'] },
  { key: 'ai-budget', name: 'AI Budget', icon: Wallet, Screen: BudgetTab, permissions: ['ai.budget.view'] },
  {
    key: 'business-opportunities',
    name: 'Business Opportunities',
    icon: BriefcaseBusiness,
    Screen: BusinessOpportunitiesTab,
    permissions: ['ai.processes.view', 'research.dimensions.view', 'research.matrix.view', 'research.prompt.view'],
  },
  { key: 'asset-catalog', name: 'Asset Catalog', icon: Library, Screen: AssetCatalogTab, permissions: ['ai.processes.view'] },
];

const BUSINESS_OPPORTUNITIES = 'business-opportunities';
const RESEARCH_PROCESS = 'discovery.business-opportunity-research';

/** Tabs that became parts of a process: old links land on the part. */
export const MOVED_TABS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  processes: { tab: BUSINESS_OPPORTUNITIES, process: RESEARCH_PROCESS, part: 'model' },
  discovery: { tab: BUSINESS_OPPORTUNITIES, process: RESEARCH_PROCESS, part: 'dimensions' },
};

/** The address an old link becomes (moved tab, or the retired `&section=`), or null when it is current. */
export function upgradedParams(params: URLSearchParams): URLSearchParams | null {
  const moved = MOVED_TABS[params.get('tab') ?? ''];
  if (moved) return new URLSearchParams(moved);

  const tab = params.get('tab');
  const section = params.get('section');
  if (section === null || tab !== BUSINESS_OPPORTUNITIES) return null;
  const next = new URLSearchParams(params);
  next.delete('section');
  next.set('part', section);
  return next;
}

/** Which screen or settings section `?tab=` opens. A section link waits for the catalogue
 *  (`awaitingSection`); anything else unknown (or not the caller's to see) opens the first
 *  screen they may see, else the first section. */
export function resolveTab(
  tabsShown: readonly ConfigTab[],
  sections: SettingsSection[],
  active: string | null,
  awaitingSection: boolean,
): { screen: ConfigTab | undefined; current: SettingsSection | undefined } {
  const asked = sections.find((s) => s.key === active);
  const screen = asked || awaitingSection ? undefined : openTab(tabsShown, active);
  const current = asked ?? (screen || awaitingSection ? undefined : sections[0]);
  return { screen, current };
}
